/**
 * Верхняя плашка команд поверх рабочей области — каркас тулбара редактора плана и
 * конструктора мебели: место у верхнего края, рамка, группы `start` слева и `end` справа.
 * На телефоне вместо них показывается `mobile`, если он задан. `onOffsetChange` сообщает,
 * с какой высоты начинаются панели под плашкой.
 */
import { useLayoutEffect, useRef, type CSSProperties, type ReactNode } from "react";

/** Зазор между плашкой и панелями под ней, px */
const GAP = 10;

export const TopToolbar = ({
	start,
	end,
	mobile,
	children,
	style,
	onOffsetChange,
}: {
	start: ReactNode;
	end?: ReactNode;
	/** Своя раскладка для узкого экрана; без неё `start` и `end` видны везде */
	mobile?: ReactNode;
	/** То, что не лежит в строке: диалоги и модальные окна плашки */
	children?: ReactNode;
	style?: CSSProperties;
	onOffsetChange?: (offset: number) => void;
}) => {
	const ref = useRef<HTMLDivElement>(null);
	useLayoutEffect(() => {
		const element = ref.current;
		if (!element || !onOffsetChange) return;
		// Измеряем внешний контейнер: в высоту входит верхний отступ до самой плашки.
		const update = () => onOffsetChange(element.getBoundingClientRect().height + GAP);
		const observer = new ResizeObserver(update);
		observer.observe(element);
		update();
		return () => observer.disconnect();
	}, [onOffsetChange]);
	const desktop = mobile ? "hidden md:flex" : "flex";
	return (
		<div ref={ref} className="pointer-events-none absolute inset-x-0 top-0 z-50 px-2 pt-2">
			<div
				className="pointer-events-auto flex min-h-12 min-w-0 items-center rounded-lg border border-border bg-chrome px-3 shadow-sm"
				style={style}
			>
				<div className={`${desktop} min-w-0 flex-1 items-center gap-1 overflow-hidden`}>
					{start}
				</div>
				{end && <div className={`${desktop} ml-auto shrink-0 items-center gap-1 pl-2`}>{end}</div>}
				{mobile && (
					<div className="flex h-8 min-w-0 flex-1 items-center gap-1 md:hidden">{mobile}</div>
				)}
				{children}
			</div>
		</div>
	);
};
